import { BasicResponseDTO } from '@dto/basicResponse.dto';
import axios, { AxiosRequestConfig, AxiosResponse } from 'axios';
import { useCallback, useRef, useState } from 'react';
import { Log } from 'src/service/log.service';
import { getApi } from 'src/util/apiCalls';
import { getErrorMessage } from 'src/util/axiosError';
import { useAbortPrevious } from './useAbortController';

export interface LatestRequestOptions {
  /** Mirrors the hook's own loading flag into state owned elsewhere (context, parent) */
  setLoading?: (loading: boolean) => void;
  /** Toast text on failure, defaults to the server message */
  errorMessage?: string;
  /** Suppresses the failure toast */
  silent?: boolean;
}

/** Runner returned by {@link useLatestRequest} */
export interface LatestRequest<T> {
  /**
   * Issues one request of its kind, aborting the previous one first
   * @param run performs the request; must pass `signal` to every call it makes
   * @param options loading mirror and failure text
   * @returns the result, or undefined when the request was superseded, unmounted or failed; failures are toasted
   */
  (run: (signal: AbortSignal) => Promise<T>, options?: LatestRequestOptions): Promise<T | undefined>;
}

/** Runner returned by {@link useLatestGet} */
export interface LatestGet<R extends BasicResponseDTO> {
  /**
   * Fetches one URL of the hook's response type, aborting the previous fetch first
   * @param url GET endpoint
   * @param options loading mirror, failure text and extra axios config
   * @returns the response payload, or undefined when the fetch was superseded, unmounted or failed; failures are toasted
   */
  (url: string, options?: LatestRequestOptions & { config?: AxiosRequestConfig; }): Promise<R['data'] | undefined>;
}

/**
 * Runs the latest request of one kind: starting a new one aborts the previous, unmount aborts the last
 * @template T what the request resolves to; one hook instance per kind of request
 * @param initialLoading flag value before the first request starts
 * @returns the runner, its loading flag and an abort for the in-flight request; a superseded request leaves the flag to its successor
 */
export function useLatestRequest<T>(initialLoading = false): [LatestRequest<T>, boolean, () => void] {
  const [nextSignal, abortCurrent] = useAbortPrevious();
  const [loading, setLoading] = useState(initialLoading);
  // the mirror of the in-flight request, so an explicit abort can clear it too
  const mirrorRef = useRef<LatestRequestOptions['setLoading']>(undefined);

  const request = useCallback<LatestRequest<T>>(async (run, options = {}) => {
    const signal = nextSignal();
    mirrorRef.current = options.setLoading;
    setLoading(true);
    options.setLoading?.(true);
    try {
      const result = await run(signal);
      // a run may finish after a newer one started; its result is stale
      return signal.aborted ? undefined : result;
    } catch (reason) {
      if (!axios.isCancel(reason) && !options.silent) Log.error(options.errorMessage ?? getErrorMessage(reason), reason as Error);
      return undefined;
    } finally {
      if (!signal.aborted) {
        setLoading(false);
        options.setLoading?.(false);
      }
    }
  }, [nextSignal]);

  const abort = useCallback(() => {
    abortCurrent();
    setLoading(false);
    mirrorRef.current?.(false);
  }, [abortCurrent]);

  return [request, loading, abort];
}

/**
 * {@link useLatestRequest} for a plain GET of one response type, resolving straight to its payload
 * @template R the response DTO; the runner resolves to its `data`
 * @param initialLoading flag value before the first request starts
 * @returns the runner, its loading flag and an abort for the in-flight request
 */
export function useLatestGet<R extends BasicResponseDTO>(initialLoading = false): [LatestGet<R>, boolean, () => void] {
  const [request, loading, abort] = useLatestRequest<AxiosResponse<R>>(initialLoading);

  const get = useCallback<LatestGet<R>>((url, options = {}) =>
    request(signal => getApi<R>(url, signal, options.config), options).then(response => response?.data.data as R['data'] | undefined),
    [request]);

  return [get, loading, abort];
}
